import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useTGTAuth, type TGTSession } from "@tgtone/auth-sdk";
import { registerAuthClient } from "@/lib/auth-client";
import {
  getAllowedRedirectHosts,
  getAppKey,
  getAuthRedirectUri,
  getCoreApiUrl,
} from "@/lib/runtime-config";
import { logger } from "@/lib/logger";
import { consumeReturnPath } from "@/services/api/auth-bridge";

interface AuthContextValue {
  session: TGTSession | null;
  loading: boolean;
  logout: () => Promise<void>;
  hasRole: (app: string, role: string) => boolean;
  authClient: ReturnType<typeof useTGTAuth>["authClient"] | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const coreApiUrl = getCoreApiUrl().replace(/\/api\/?$/, "");
  const appKey = getAppKey();
  const redirectUri = getAuthRedirectUri();

  const { session, loading, logout, hasRole, authClient } = useTGTAuth({
    coreApiUrl,
    appDomain: typeof window !== "undefined" ? window.location.host : "",
    appKey,
    sessionCacheUrl: "https://session.tgtone.cl",
    heartbeatIntervalMs: 5 * 60 * 1000,
    debug: import.meta.env.DEV,
    popupAuthEnabled: false,
    redirectUri,
    allowedRedirectHosts: getAllowedRedirectHosts(redirectUri),
    onAuthSuccess: () => {
      logger.info("[Auth] Sesión activa");
      // Si veníamos de una recuperación forzada por sesión sin refresh token
      // (ver client.ts), volvemos exactamente a la página donde se cortó en
      // vez de dejar al usuario en la raíz de la app.
      const returnPath = consumeReturnPath();
      const currentPath = window.location.pathname + window.location.search;
      if (returnPath && returnPath !== currentPath) {
        window.location.replace(returnPath);
      }
    },
    onAuthFailure: (error) => {
      logger.warn("[Auth] Error de autenticación:", error);
      // En desarrollo, no redirigir automáticamente al login
      // El ProtectedApp se encarga de mostrar spinner o recovery
    },
    onSessionRevoked: (error) => {
      logger.warn("[Auth] Sesión revocada:", error);
    },
  });

  // Mantener el bridge alineado con la instancia que realmente posee la sesión.
  // No se crea ni refresca una segunda sesión fuera del SDK.
  registerAuthClient(authClient);

  // Log URLs at startup
  useEffect(() => {
    logger.info("[Runtime] CORE_API_URL:", coreApiUrl);
    logger.info("[Runtime] APP_KEY:", appKey);
    logger.info("[Runtime] REDIRECT_URI:", redirectUri);
    logger.info("[Auth] Estado de renovación:", {
      hasAccessToken: Boolean(authClient.getToken()),
      hasRefreshToken: authClient.hasRefreshToken(),
    });
  }, [appKey, authClient, coreApiUrl, redirectUri, session]);

  const value: AuthContextValue = {
    session,
    loading,
    logout,
    hasRole,
    authClient,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}

/**
 * ProtectedApp: muestra spinner mientras carga, renderiza children solo con sesión activa.
 * Para TanStack Start, esto va en el root route.
 */
export function ProtectedApp({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();

  // El SDK es la única autoridad para callback, refresh y redirección SSO.
  if (session) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // No session, no loading — show spinner while OAuth redirects or recovery fires
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
    </div>
  );
}
